import { type ReactElement, useState } from 'react';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { getDialogMessage } from '@/shared/copy/dialogs';
import { getFormLabel } from '@/shared/copy/forms';
import { getToastMessage } from '@/shared/copy/toasts';
import { Box, DialogFormBody, Panel, Stack, Text } from '@/ui';
import { settingsPanelSurfaceSx } from '@/ui/composites/settingsPageTypographySx';
import { useDialogFormState } from '@/ui/hooks/useDialogFormState';
import { useFormErrors } from '@/ui/hooks/useFormErrors';
import { Button } from '@/ui/primitives/Button';
import { TextField } from '@/ui/primitives/TextField';
import {
  buildProjectSettingsDestructiveActionButtonSx,
  projectSettingsDestructiveActionRowSx,
  projectSettingsDestructiveDescriptionCellSx,
  projectSettingsConfirmationCodeSx,
} from './projectSettingsDestructiveSurfaceSx';
import { ProjectSettingsHelperText } from './ProjectSettingsHelperText';
import type { ProjectDto } from '@flowqa/shared';

type MutationErrorController = { clearError: () => void; setError: (err: unknown) => void };
type NotifyInput = { message: string; tone: 'green' | 'blue' | 'red' };

export type ProjectDataWipeSectionProps = {
  project: ProjectDto | null;
  deleteProject: {
    isPending: boolean;
    mutate: (
      input: { id: string },
      options?: {
        onSuccess?: () => void;
        onError?: (err: unknown) => void;
      },
    ) => void;
  };
  wipeProjectData: {
    isPending: boolean;
    mutate: (
      input: {
        id: string;
        confirmation: string;
        reasonCode?: string;
        ticket?: string;
        async?: boolean;
      },
      options?: {
        onSuccess?: (result: {
          status: 'wiped' | 'scheduled';
          jobId?: string;
          jobStatus?: 'pending' | 'running' | 'completed' | 'failed';
          recoveryHint?: string;
        }) => void;
        onError?: (err: unknown) => void;
      },
    ) => void;
  };
  mutationError: MutationErrorController;
  notify: (input: NotifyInput) => void;
  onProjectDeleted: () => void;
};

export const ProjectDataWipeSection = ({
  project,
  deleteProject,
  wipeProjectData,
  mutationError,
  notify,
  onProjectDeleted,
}: ProjectDataWipeSectionProps): ReactElement => {
  const destructiveActionButtonSx = buildProjectSettingsDestructiveActionButtonSx({
    width: { sm: '132px' },
  });

  const [wipeOpen, setWipeOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const wipeForm = useDialogFormState({ projectName: '' });
  const deleteForm = useDialogFormState({ projectName: '' });
  const wipeErrors = useFormErrors(wipeForm);
  const deleteErrors = useFormErrors(deleteForm);
  const expectedProjectName = project?.name.trim() ?? '';
  const expectedWipeConfirmation = project ? `WIPE_PROJECT_DATA:${project.id}` : '';
  const wipeProjectNameMatches =
    expectedProjectName.length > 0 && wipeForm.values.projectName.trim() === expectedProjectName;
  const deleteProjectNameMatches =
    expectedProjectName.length > 0 && deleteForm.values.projectName.trim() === expectedProjectName;

  const closeWipeDialog = (): void => {
    if (wipeProjectData.isPending || deleteProject.isPending) return;
    wipeForm.handleClose(
      () => {
        setWipeOpen(false);
      },
      { clearError: mutationError.clearError },
    );
  };

  const closeDeleteDialog = (): void => {
    if (deleteProject.isPending || wipeProjectData.isPending) return;
    deleteForm.handleClose(
      () => {
        setDeleteOpen(false);
      },
      { clearError: mutationError.clearError },
    );
  };

  const confirmExpectedProjectName = (form: typeof wipeForm | typeof deleteForm): boolean => {
    const valid = form.validateRequired('projectName');
    if (!valid) return false;
    if (form.values.projectName.trim() === expectedProjectName) return true;
    form.setFieldError('projectName', 'Enter the exact project name to confirm.');
    return false;
  };

  return (
    <Panel title="Project Deletion" surfaceVariant="settings" sx={settingsPanelSurfaceSx}>
      <Stack gap="xs">
        <Stack gap="md">
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            align={{ sm: 'flex-start' }}
            sx={projectSettingsDestructiveActionRowSx}
          >
            <Box sx={projectSettingsDestructiveDescriptionCellSx}>
              <ProjectSettingsHelperText>
                Delete all data associated with this project. Includes all module data. Does not
                delete the project.
              </ProjectSettingsHelperText>
            </Box>
            <Button
              variant="secondary"
              color="error"
              fullWidthOnMobile={false}
              sx={destructiveActionButtonSx}
              disabled={!project || wipeProjectData.isPending || deleteProject.isPending}
              onClick={() => {
                mutationError.clearError();
                wipeForm.resetDialog();
                setWipeOpen(true);
              }}
            >
              Delete Data
            </Button>
          </Stack>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            align={{ sm: 'flex-start' }}
            sx={projectSettingsDestructiveActionRowSx}
          >
            <Box sx={projectSettingsDestructiveDescriptionCellSx}>
              <ProjectSettingsHelperText>
                Delete the project from workspace. Includes all data associated with this project.
              </ProjectSettingsHelperText>
            </Box>
            <Button
              variant="secondary"
              color="error"
              fullWidthOnMobile={false}
              sx={destructiveActionButtonSx}
              disabled={!project || deleteProject.isPending || wipeProjectData.isPending}
              onClick={() => {
                mutationError.clearError();
                deleteForm.resetDialog();
                setDeleteOpen(true);
              }}
            >
              Delete Project
            </Button>
          </Stack>
        </Stack>
        <ConfirmDialog
          open={wipeOpen}
          title={getDialogMessage('destructive.deleteNamed.title', { label: 'Project Data' })}
          description="This action is permanent and cannot be undone. It will remove all project data."
          confirmLabel={getDialogMessage('destructive.deleteNamed.confirmLabel', {
            label: 'Data',
          })}
          confirmColor="error"
          confirmVariant="contained"
          confirmDisabled={!wipeProjectNameMatches}
          loading={wipeProjectData.isPending || deleteProject.isPending}
          onClose={closeWipeDialog}
          onConfirm={() => {
            if (!project || !confirmExpectedProjectName(wipeForm)) return;
            wipeProjectData.mutate(
              {
                id: project.id,
                confirmation: expectedWipeConfirmation,
                reasonCode: 'USER_REQUEST',
                async: true,
              },
              {
                onSuccess: (result) => {
                  mutationError.clearError();
                  wipeForm.resetDialog();
                  setWipeOpen(false);
                  if (result.status === 'scheduled') {
                    notify({
                      message: getToastMessage('project.dataWipeScheduled', {
                        name: project.name,
                      }),
                      tone: 'blue',
                    });
                    return;
                  }
                  notify({
                    message: getToastMessage('project.dataWiped', { name: project.name }),
                    tone: 'green',
                  });
                },
                onError: (err) => {
                  mutationError.setError(err);
                },
              },
            );
          }}
        >
          <DialogFormBody>
            <Text role="body" color="textSecondary">
              Enter{' '}
              <Box component="code" sx={projectSettingsConfirmationCodeSx}>
                {expectedProjectName}
              </Box>{' '}
              to confirm.
            </Text>
            <TextField
              label={getFormLabel('projectName')}
              fieldStyle="settings"
              value={wipeForm.values.projectName}
              onChange={wipeForm.handleChange('projectName')}
              {...wipeErrors.fieldErrorProps('projectName')}
              fullWidth
            />
          </DialogFormBody>
        </ConfirmDialog>
        <ConfirmDialog
          open={deleteOpen}
          title={getDialogMessage('destructive.delete.title', { entity: 'project' })}
          description="This action is permanent and cannot be undone."
          confirmLabel={getDialogMessage('destructive.delete.confirmLabel', { entity: 'project' })}
          confirmColor="error"
          confirmVariant="contained"
          confirmDisabled={!deleteProjectNameMatches}
          loading={deleteProject.isPending || wipeProjectData.isPending}
          onClose={closeDeleteDialog}
          onConfirm={() => {
            if (!project || !confirmExpectedProjectName(deleteForm)) return;
            deleteProject.mutate(
              { id: project.id },
              {
                onSuccess: () => {
                  mutationError.clearError();
                  deleteForm.resetDialog();
                  setDeleteOpen(false);
                  notify({
                    message: getToastMessage('entity.deleted', {
                      entity: 'project',
                      name: project.name,
                    }),
                    tone: 'green',
                  });
                  onProjectDeleted();
                },
                onError: (err) => {
                  mutationError.setError(err);
                },
              },
            );
          }}
        >
          <DialogFormBody>
            <Text role="body" color="textSecondary">
              Enter{' '}
              <Box component="code" sx={projectSettingsConfirmationCodeSx}>
                {expectedProjectName}
              </Box>{' '}
              to confirm.
            </Text>
            <TextField
              label={getFormLabel('projectName')}
              fieldStyle="settings"
              value={deleteForm.values.projectName}
              onChange={deleteForm.handleChange('projectName')}
              {...deleteErrors.fieldErrorProps('projectName')}
              fullWidth
            />
          </DialogFormBody>
        </ConfirmDialog>
      </Stack>
    </Panel>
  );
};
